<template>
  <div>
    <div v-if="spot.archivedOn"
         class="mb-3 p-1 bg-theme-secondary">
      <i18n-t keypath="spotdetail.spot_was_archived_text"
              tag="h3"
              class="text-white text-center"
              scope="global">
        <template v-slot:date>{{ $d(spot.archivedOn) }}
        </template>
      </i18n-t>
    </div>
    <div class="flex flex-col sm:flex-row mx-1 sm:mx-2 sm:gap-2">
      <h1 class="mb-2 grow sm:text-center">{{ spot.title }}</h1>
      <SpotDetailsFollow v-if="spot.userData"
                         :spot="spot"
                         :follows="spot.userData?.follows" />
    </div>
    <div class="flex flex-col gap-1 max-w-2xl mx-auto px-1">
      <SpotDetailsVote :spot="spot"
                       :voted="spot.userData?.voted" />
      <div class="h-[400px] sm:h-[530px]">
        <div class="absolute h-[400px] sm:h-[530px] left-0 right-0 flex justify-start overflow-y-hidden">
          <div id="carousel"
               class="moveable-gallery min-w-full flex gap-3 flex-nowrap overflow-x-auto snap-x snap-mandatory"
               @scroll="carouselDidScroll()">
            <div class="flex-shrink-0 w-[40%]"></div>
            <div class="carousel-element flex flex-col items-end flex-grow-0 min-w-fit"
                 v-for="(image, index) in spot.gallery">
              <img class="snap-center h-[370px] sm:h-[500px] object-contain"
                   :id="'image-' + index"
                   :src="useImageUrl(image.imageId, 'large')"
                   @click="scroll(index)"
                   :alt="$t('accessibility.gallery_image')" />
              <div v-if="image.showsFix && image.ownerApprovesFix === false"
                   class="flex justify-center items-center w-full h-[26px] -mt-[26px] bg-theme-font-console">
                <p class="text-sm text-white uppercase font-semibold ">{{ $t('spotdetail.fix_declined') }}</p>
              </div>
              <div v-else-if="image.showsFix"
                   class="flex justify-center items-center w-full h-[26px] -mt-[26px] bg-theme-good ">
                <p class="text-sm text-white uppercase font-semibold ">fixed</p>
              </div>
              <p>
                <NuxtLink v-if="image.organizationName"
                          :to="image.organizationSlug ? '/partners/'+ image.organizationSlug : '/users/' + image.userName"
                          class="text-theme-primary underline">{{
                    image.organizationName
                  }}
                </NuxtLink>
                <NuxtLink v-else-if="image.userName"
                          :to="'/users/' + image.userName"
                          class="text-theme-primary underline">{{
                    image.userName
                  }}
                </NuxtLink>
                <span v-else>{{ $t('spotdetail.deleted_user_title') }}</span>
                ,
                {{ $d(new Date(image.uploadedOn)) }}
              </p>
            </div>
            <div class="w-[40%] flex-shrink-0"></div>
          </div>
        </div>
      </div>
      <div class="flex mb-2 justify-center items-center ">
        <button class="w-[20px] h-[20px]"
                v-for="(image, index) in spot.gallery"
                @click="scroll(index)"
                :title="$t('accessibility.scroll_to_image_nr') + ' ' + (index + 1)"><span
          class="dot block bg-theme-secondary/60 rounded-[5px] m-[5px] w-[10px] h-[10px] hover:bg-theme-secondary active:bg-theme-secondary"></span>
        </button>
      </div>
      <SpotDetailsGotFixed v-if="spot.gotFixed"
                           :spot="spot" />
      <div>
        <p class="spot-description mb-2 leading-6"
           v-linkify:options="{
            target: '_blank'
          }"
           v-html="textHelpers.replaceNewlineWithHtmlTags(spot.text)">
        </p>
      </div>
      <!--      Steckbrief -->
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 divide-y-[1px] divide-theme-secondary">
        <div class="sm:col-span-2">
          <NuxtLink v-if="spot.user"
                    :to="spot.user.organizationSlug ? '/partners/'+encodeURI(spot.user.organizationSlug) : '/users/' + encodeURI(spot.user.userName)"
                    class="flex flex-row items-center gap-1">
            <UserImage :size="'md'"
                       :imageId="spot.user.profileImageId" />
            <div class="flex gap-1">
              <p class="font-semibold text-3xl">{{
                  spot.user.organizationName ? spot.user.organizationName : spot.user.userName
                }}</p>
              <img v-if="spot.user.verified"
                   src="~assets/icon/verified.svg"
                   :alt="$t('accessibility.verified')" />
            </div>
          </NuxtLink>
          <div v-else
               class="flex flex-row items-center gap-1">
            <UserImage :size="'md'" />
            <div class="flex gap-1">
              <p class="font-semibold text-3xl">{{ $t('spotdetail.deleted_user_title') }}</p>
            </div>
          </div>
        </div>
        <div class="pt-2">
          <button @click="showAddressModal()"
                  class="flex items-start"><span
            class="text-left text-theme-primary underline hover:text-theme-secondary">{{ spot.addressString
            }}</span>
          </button>
          <ClientOnly>
            <SpotDetailsAddressModal id="address-modal"
                                     :spot="spot"
                                     @close="addressModalVisible = false"
                                     :open="addressModalVisible" />
          </ClientOnly>
        </div>
        <div class="pt-2">
          <p>{{ $t('spotdetail.created_on') }}: {{ $d(new Date(spot.createdOn)) }}</p>
        </div>
        <div class="pt-1">
          <div class="flex items-center mb-1">
            <p class="font-bold">{{ $t('spotdetail.categories_title') }}</p>
            <ButtonQuestionMark>
              <p class="text-sm">
                {{ $t('spotdetail.categories_explanation') }}
              </p>
            </ButtonQuestionMark>
          </div>
          <div class="flex items-end">
            <div class="flex gap-1 flex-wrap">
              <BadgeCategory v-for="category in spot.categories"
                             :category="category"
                             :spot="spot"
                             :showAddedByOther="spot.userData?.spotAuthor" />
            </div>
            <div v-if="categoriesContainAddedByOther && spot.userData?.spotAuthor">
              <ButtonQuestionMark>
                <p class="text-sm">
                  * {{ $t('spotdetail.category_added_by_other_text') }}
                </p>
              </ButtonQuestionMark>
            </div>
          </div>
        </div>
        <div class="pt-1">
          <div class="flex items-center mb-1">
            <p class="font-bold">{{ $t('spotdetail.organisations_title') }}</p>
            <ButtonQuestionMark>
              <i18n-t
                keypath="spotdetail.organisations_explanation"
                scope="global"
                tag="p"
                class="text-sm"
              >
                <template v-slot:platform>{{ usePlatformSettings().platformName }}
                </template
                >
                {{ $t('spotdetail.organisations_explanation') }}
              </i18n-t>
            </ButtonQuestionMark>
          </div>
          <div class="flex gap-1 flex-wrap">
            <BadgeOrganization v-for="organization in spot.organizations"
                               :organization="organization" />
          </div>
        </div>
        <div class="invisible sm:visible"></div>
        <div class="invisible sm:visible"></div>
      </div>
      <SpotDetailsArchive :spot="spot" />
      <div v-if="authenticated"
           class="pt-2 grid sm:grid-cols-2 sm:col-span-2 gap-2">
        <div v-if="authenticated && !spot.userData?.spotAuthor">
          <ButtonPrimary class="w-full"
                         onclick="add_category_modal.showModal();">{{ $t('spotdetail.add_category') }}
          </ButtonPrimary>
          <Modal id="add_category_modal">
            <div class="flex flex-col gap-2">
              <h2>{{ $t('spotdetail.add_category') }}</h2>
              <SpotDetailsAddCategory :spot="spot"
                                      @success="closeAddCategoryModal()" />
            </div>
          </Modal>
        </div>
        <div>
          <ButtonPrimary class="w-full"
                         onclick="add_image_modal.showModal()">{{ $t('spotdetail.add_image_title') }}
          </ButtonPrimary>
          <Modal id="add_image_modal">
            <div class="flex flex-col gap-2">
              <h2>{{ $t('spotdetail.add_image_title') }}</h2>
              <SpotDetailsAddImage :spot="spot"
                                   name="add-image"
                                   @success="closeAddImageModal()" />
            </div>
          </Modal>
        </div>
        <div v-if="authenticated && spot.userData?.spotAuthor">
          <NuxtLink :to="'/spots/' + spot.id + '/edit'"
                    class="grow">
            <ButtonPrimary class="w-full">{{
                $t('spotdetail.edit_spot_title')
              }}
            </ButtonPrimary>
          </NuxtLink>
        </div>
        <div v-if="authenticated && spot.userData?.spotAuthor && !spot.gotFixed && !spot.famed">
          <ButtonPrimary class="w-full"
                         onclick="set_fixed_modal.showModal()">{{ $t('spotdetail.spot_fixed_button_title') }}
          </ButtonPrimary>
        </div>

        <div v-if="authenticated && spot.userData?.spotAuthor">
          <ButtonPrimary class="w-full"
                         color="red"
                         onclick="delete_spot_modal.showModal()">{{
              $t('spotdetail.delete_spot_button_text') }}
          </ButtonPrimary>
          <Modal id="delete_spot_modal">
            <SpotDetailsDelete :spot="spot"
                               @close="closeDeleteSpotModal()" />
          </Modal>
        </div>
      </div>
    </div>
    <SpotDetailsFixProposals v-if="showFixOption"
                             :spot="spot" />
    <div class="flex flex-col gap-1 max-w-2xl mx-auto">
      <SpotOfficialAnswer v-if="spot.officialAnswer"
                          :spot="spot"
                          :officialAnswer="spot.officialAnswer" />
    </div>
  </div>
</template>
<script setup lang="ts">

const props = defineProps<{ spot: SpotDetail }>();
const authenticated = useAuth().authenticated;
let ignoreScroll = false;
const categoriesContainAddedByOther = computed(() => {
  return props.spot.categories.some(
    (category: Category) => {
      if (category.addedBy) {
        return category.addedBy.id != props.spot.user?.id;
      } else {
        return false;
      }
    }
  );
});

const addressModalVisible = ref(false);

const showFixOption = computed(() => {
  return (
    authenticated.value &&
    !props.spot.archivedOn &&
    !props.spot.famed &&
    !props.spot.gotFixed
  );
});


function closeAddCategoryModal() {
  (document.getElementById('add_category_modal') as HTMLFormElement).close();
}

function closeAddImageModal() {
  (document.getElementById('add_image_modal') as HTMLFormElement).close();
}

function closeDeleteSpotModal() {
  (document.getElementById('delete_spot_modal') as HTMLFormElement).close();
}

function showAddressModal() {
  addressModalVisible.value = true;
}

function scroll(index: number) {
  ignoreScroll = true;
  document.getElementById(`image-${index}`)!.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  selectDot(index);
  ignoreScroll = false;

}

function selectDot(index: number) {
  const dots = document.getElementsByClassName('dot');
  for (let i = 0; i < dots.length; i++) {
    if (i === index) {
      dots[i].classList.remove('bg-theme-secondary/60');
      dots[i].classList.add('bg-theme-secondary');
    } else {
      dots[i].classList.remove('bg-theme-secondary');
      dots[i].classList.add('bg-theme-secondary/60');
    }
  }
}

function carouselDidScroll() {
  if (ignoreScroll) {
    return;
  }
  const images = document.getElementsByClassName('carousel-element');

  for (let i = 0; i < images.length; i++) {
    if (images[i].getBoundingClientRect().x < window.innerWidth / 2 && images[i].getBoundingClientRect().right > window.innerWidth / 2) {
      selectDot(i);
      break;
    }
  }
}

</script>
<style scoped lang="postcss">
:deep(.spot-description) {
  a {
    @apply text-theme-primary underline;
  }
}

.moveable-gallery::-webkit-scrollbar {
  display: none;
}

.moveable-gallery {
  -ms-overflow-style: none;
  /* IE and Edge */
  scrollbar-width: none;
  /* Firefox */
}
</style>
